import { Controller } from "@hotwired/stimulus";
import {
    applyWorkflowStepNavWidthPx,
    applyStoredWorkflowStepNavWidth,
    clampWorkflowStepNavWidthPx,
    readStoredWorkflowStepNavWidthPx,
    WORKFLOW_STEP_NAV_WIDTH_MAX,
    WORKFLOW_STEP_NAV_WIDTH_MIN,
} from "../utils/workflow_step_nav_width_sync";

export const WORKFLOW_STEP_NAV_WIDTH_DEFAULT = 360;
const KEYBOARD_STEP = 16;

export default class extends Controller {
    static targets = ["handle"];

    connect() {
        applyStoredWorkflowStepNavWidth();
        this.syncNarrowClass(this.widthForNarrowClass());
        document.addEventListener("depozen:workflow-step-nav-layout-change", this.boundSyncAfterLayoutChange);
    }

    start(event) {
        event.preventDefault();
        this.dragging = true;
        this.startX = event.clientX;
        this.startWidth = this.currentWidth();
        this.element.classList.add("workflow-step-shell--resizing");
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";

        document.addEventListener("pointermove", this.boundMove);
        document.addEventListener("pointerup", this.boundStop);
        document.addEventListener("pointercancel", this.boundStop);
    }

    disconnect() {
        this.stopDrag();
        document.removeEventListener("depozen:workflow-step-nav-layout-change", this.boundSyncAfterLayoutChange);
    }

    boundSyncAfterLayoutChange = () => {
        if (this.isCollapsed()) {
            this.element.classList.remove("workflow-step-shell--nav-narrow");
            return;
        }

        this.element.classList.remove("workflow-step-shell--nav-narrow");

        const apply = () => this.syncNarrowClass(this.widthForNarrowClass());

        requestAnimationFrame(() => {
            requestAnimationFrame(apply);
        });
        window.setTimeout(apply, 220);
    };

    widthForNarrowClass() {
        const measured = this.currentWidth();
        const stored = readStoredWorkflowStepNavWidthPx() ?? WORKFLOW_STEP_NAV_WIDTH_DEFAULT;

        // Pendant l’animation collapsed → opened, la largeur mesurée reste ~3.5rem :
        // ne pas en déduire un mode narrow (masque titre / badges).
        if (measured <= 80) return stored;

        return measured;
    }

    boundMove = (event) => {
        if (!this.dragging) return;

        const rawWidth = this.startWidth + (event.clientX - this.startX);

        if (this.isCollapsed()) {
            if (rawWidth < this.expandAtWidth()) return;

            const width = this.clamp(rawWidth);
            this.setWidth(width, { persist: false, instant: true });
            this.expandFromDrag();
            return;
        }

        if (rawWidth < WORKFLOW_STEP_NAV_WIDTH_MIN) {
            if (!this.isCollapsed()) this.collapseFromDrag();
            return;
        }

        this.setWidth(this.clamp(rawWidth), { persist: false });
    };

    boundStop = () => {
        if (!this.dragging) return;

        this.dragging = false;
        this.element.classList.remove("workflow-step-shell--resizing");
        document.body.style.cursor = "";
        document.body.style.userSelect = "";

        document.removeEventListener("pointermove", this.boundMove);
        document.removeEventListener("pointerup", this.boundStop);
        document.removeEventListener("pointercancel", this.boundStop);

        if (!this.isCollapsed()) {
            this.setWidth(this.currentWidth(), { persist: true });
        }
    };

    stopDrag() {
        if (!this.dragging) return;
        this.boundStop();
    }

    keydown(event) {
        if (!this.hasHandleTarget) return;

        if (this.isCollapsed()) {
            if (event.key === "ArrowRight" || event.key === "End") {
                event.preventDefault();
                this.expandFromDrag();
                const width =
                    event.key === "End"
                        ? WORKFLOW_STEP_NAV_WIDTH_MAX
                        : this.clamp(readStoredWorkflowStepNavWidthPx() ?? WORKFLOW_STEP_NAV_WIDTH_DEFAULT);
                this.setWidth(width, { persist: true });
            }
            return;
        }

        let width = this.currentWidth();
        let handled = true;

        switch (event.key) {
            case "ArrowLeft":
                width -= KEYBOARD_STEP;
                break;
            case "ArrowRight":
                width += KEYBOARD_STEP;
                break;
            case "Home":
                width = WORKFLOW_STEP_NAV_WIDTH_MIN;
                break;
            case "End":
                width = WORKFLOW_STEP_NAV_WIDTH_MAX;
                break;
            default:
                handled = false;
        }

        if (!handled) return;

        event.preventDefault();
        this.setWidth(this.clamp(width), { persist: true });
    }

    reset(event) {
        event.preventDefault();
        applyWorkflowStepNavWidthPx(null, { persist: true });
        this.updateHandleAria(WORKFLOW_STEP_NAV_WIDTH_DEFAULT);
        this.syncNarrowClass(WORKFLOW_STEP_NAV_WIDTH_DEFAULT);
    }

    currentWidth() {
        const nav = this.element.querySelector(".workflow-step-shell__nav");
        return nav?.getBoundingClientRect().width ?? WORKFLOW_STEP_NAV_WIDTH_DEFAULT;
    }

    expandAtWidth() {
        return WORKFLOW_STEP_NAV_WIDTH_MIN;
    }

    clamp(width) {
        return clampWorkflowStepNavWidthPx(width);
    }

    setWidth(px, { persist, instant = !persist }) {
        const clamped = applyWorkflowStepNavWidthPx(px, { persist, instant });
        if (clamped != null) {
            this.updateHandleAria(clamped);
            this.syncNarrowClass(clamped);
        }
    }

    syncNarrowClass(width) {
        if (this.isCollapsed()) {
            this.element.classList.remove("workflow-step-shell--nav-narrow");
            return;
        }

        this.element.classList.toggle("workflow-step-shell--nav-narrow", width <= WORKFLOW_STEP_NAV_WIDTH_MIN);
    }

    collapseFromDrag() {
        this.element.classList.remove("workflow-step-shell--nav-narrow");
        this.element.dispatchEvent(
            new CustomEvent("depozen:workflow-step-nav-collapse-from-resize", { bubbles: false }),
        );
    }

    expandFromDrag() {
        this.element.dispatchEvent(
            new CustomEvent("depozen:workflow-step-nav-expand-from-resize", { bubbles: false }),
        );
    }

    updateHandleAria(width) {
        if (!this.hasHandleTarget) return;

        const resolved = width ?? readStoredWorkflowStepNavWidthPx() ?? WORKFLOW_STEP_NAV_WIDTH_DEFAULT;
        this.handleTarget.setAttribute("aria-valuenow", String(resolved));
        this.handleTarget.setAttribute("aria-valuemin", String(WORKFLOW_STEP_NAV_WIDTH_MIN));
        this.handleTarget.setAttribute("aria-valuemax", String(WORKFLOW_STEP_NAV_WIDTH_MAX));
    }

    isCollapsed() {
        return (
            document.documentElement.dataset.workflowStepNavLayout === "collapsed" ||
            this.element.dataset.workflowStepNavLayout === "collapsed"
        );
    }
}
